import React, { useState, useRef, useEffect } from 'react';
import './LBSelect.scss';

const LBSelect = ({
  options = [],
  value = null,
  onChange = () => {},
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  multiSelect = false,
  searchable = false,
  size = 'medium', // 'small', 'medium', 'large'
  color = 'blue', // 'blue', 'green', 'gray', 'red', 'purple'
  label = '',
  disabled = false,
  required = false,
  error = '',
  maxHeight = '400px',
  minHeight = '200px',
  className = '',
  renderOption = null,
  renderSelectedItem = null,
  closeOnSelect = true,
  clearable = false,
  loading = false,
  noOptionsText = 'No options found',
  maxSelectedDisplay = 4,
  variant = null,
  selectAll = false,
  showTags = true,
  borderless = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const optionsRef = useRef(null);

  // Variant configurations
  const variantStyles = {
    default: {
      multiSelect: false,
      searchable: false,
      closeOnSelect: true,
      required: false,
      clearable: true,
      size: 'medium',
      color: 'blue'
    },
    multiSearch: {
      multiSelect: true,
      searchable: true,
      closeOnSelect: false,
      clearable: true,
      size: 'medium',
      color: 'blue'
    }
  }

  // Get effective values (variant overrides original props)
  const variantConfig = variant && variantStyles[variant] ? variantStyles[variant] : {};
  const effectiveMultiSelect = variantConfig.multiSelect ?? multiSelect;
  const effectiveSearchable = variantConfig.searchable ?? searchable;
  const effectiveCloseOnSelect = variantConfig.closeOnSelect ?? closeOnSelect;
  const effectiveRequired = variantConfig.required ?? required;
  const effectiveClearable = variantConfig.clearable ?? clearable;
  const effectiveSize = variantConfig.size ?? size;
  const effectiveColor = variantConfig.color ?? color;

  // Normalize options to have consistent structure
  const normalizedOptions = options.map((option, index) => {
    if (typeof option === 'string' || typeof option === 'number') {
      return { label: option.toString(), value: option, id: index };
    }
    return { ...option, id: option.id || index };
  });

  // Filter options based on search term
  const filteredOptions = effectiveSearchable && searchTerm
    ? normalizedOptions.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : normalizedOptions;

  // Select All functionality
  const selectableFilteredOptions = filteredOptions.filter(option => !option.disabled);
  const currentValue = Array.isArray(value) ? value : [];
  const allFilteredSelected = selectableFilteredOptions.length > 0 && 
    selectableFilteredOptions.every(option => currentValue.includes(option.value));

  // Get selected options
  const selectedOptions = effectiveMultiSelect
    ? normalizedOptions.filter(option => 
        Array.isArray(value) && value.includes(option.value)
      )
    : normalizedOptions.find(option => option.value === value);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      const hasSelectAll = selectAll && effectiveMultiSelect && selectableFilteredOptions.length > 0;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev => {
            if (hasSelectAll && prev === -1) {
              // Move from Select All to first regular option
              let nextIndex = 0;
              while (filteredOptions[nextIndex]?.disabled && nextIndex < filteredOptions.length - 1) {
                nextIndex++;
              }
              return nextIndex;
            }
            let nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : (hasSelectAll ? -1 : 0);
            // Skip disabled options
            while (filteredOptions[nextIndex]?.disabled && nextIndex !== prev) {
              nextIndex = nextIndex < filteredOptions.length - 1 ? nextIndex + 1 : (hasSelectAll ? -1 : 0);
              if (nextIndex === -1) break; // Select All is never disabled
            }
            return nextIndex;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => {
            if (hasSelectAll && prev === 0) {
              // Move from first option to Select All
              return -1;
            }
            let nextIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1;
            // Skip disabled options
            while (filteredOptions[nextIndex]?.disabled && nextIndex !== prev) {
              nextIndex = nextIndex > 0 ? nextIndex - 1 : filteredOptions.length - 1;
            }
            return nextIndex;
          });
          break;
        case 'Enter':
          event.preventDefault();
          if (hasSelectAll && highlightedIndex === -1) {
            handleSelectAll();
          } else if (highlightedIndex >= 0 && filteredOptions[highlightedIndex] && !filteredOptions[highlightedIndex].disabled) {
            handleOptionClick(filteredOptions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSearchTerm('');
          setHighlightedIndex(-1);
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, highlightedIndex, filteredOptions, selectAll, effectiveMultiSelect]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current) {
      const highlightedElement = optionsRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex]);

  const handleOptionClick = (option) => {
    if (option.disabled) return;
    
    if (effectiveMultiSelect) {
      const currentValue = Array.isArray(value) ? value : [];
      const newValue = currentValue.includes(option.value)
        ? currentValue.filter(v => v !== option.value)
        : [...currentValue, option.value];
      onChange(newValue);
      
      if (!effectiveCloseOnSelect) {
        setSearchTerm('');
      } else {
        setIsOpen(false);
        setSearchTerm('');
      }
    } else {
      onChange(option.value);
      setIsOpen(false);
      setSearchTerm('');
    }
    // Don't highlight Select All after clicking an option
    if (effectiveMultiSelect && !effectiveCloseOnSelect) {
      // Keep the current highlight or reset to no highlight
      setHighlightedIndex(highlightedIndex >= 0 ? highlightedIndex : 0);
    } else {
      setHighlightedIndex(-1);
    }
  };

  const handleRemoveSelected = (valueToRemove) => {
    if (effectiveMultiSelect && Array.isArray(value)) {
      const newValue = value.filter(v => v !== valueToRemove);
      onChange(newValue);
    }
  };

  const handleClear = () => {
    onChange(effectiveMultiSelect ? [] : null);
  };

  const handleSelectAll = () => {
    if (!effectiveMultiSelect) return;
    
    if (allFilteredSelected) {
      // Deselect all filtered options
      const filteredValues = selectableFilteredOptions.map(option => option.value);
      const newValue = currentValue.filter(v => !filteredValues.includes(v));
      onChange(newValue);
    } else {
      // Select all filtered options
      const filteredValues = selectableFilteredOptions.map(option => option.value);
      const newValue = [...new Set([...currentValue, ...filteredValues])];
      onChange(newValue);
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && effectiveSearchable) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  };

  const getDisplayText = () => {
    if (effectiveMultiSelect && Array.isArray(value) && value.length > 0) {
      if (value.length <= maxSelectedDisplay) {
        return `${value.length} selected`;
      } else {
        return `${value.length} selected`;
      }
    } else if (!effectiveMultiSelect && selectedOptions) {
      return selectedOptions.label;
    }
    return placeholder;
  };

  const renderSelectedTags = () => {
    if (!effectiveMultiSelect || !Array.isArray(value) || value.length === 0) {
      return null;
    }

    const displayedTags = selectedOptions.slice(0, maxSelectedDisplay);
    const remainingCount = selectedOptions.length - maxSelectedDisplay;

    return (
      <div className="LB-select__tags">
        {displayedTags.map((option) => (
          <span
            key={option.value}
            className={`LB-select__tag LB-select__tag--${effectiveColor}`}
          >
            {renderSelectedItem ? renderSelectedItem(option) : option.label}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveSelected(option.value);
              }}
              className="LB-select__tag-remove"
              aria-label={`Remove ${option.label}`}
            >
              ×
            </button>
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="LB-select__tag LB-select__tag--count">
            +{remainingCount} more
          </span>
        )}
      </div>
    );
  };

  const sizeClass = `LB-select--${effectiveSize}`;
  const colorClass = `LB-select--${effectiveColor}`;
  const stateClass = error ? 'LB-select--error' : '';
  const borderlessClass = borderless ? 'LB-select--borderless' : '';

  return (
    <div className={`LB-select ${className}`} {...props}>
      {label && (
        <label className="LB-select__label">
          {label}
          {effectiveRequired && <span className="LB-select__required">*</span>}
        </label>
      )}
      
      <div
        ref={dropdownRef}
        className={`LB-select__container ${sizeClass} ${colorClass} ${stateClass} ${borderlessClass} ${
          disabled ? 'LB-select__container--disabled' : ''
        } ${isOpen ? 'LB-select__container--open' : ''}`}
      >
        <div
          className="LB-select__input-wrapper"
          onClick={toggleDropdown}
        >
          <div className="LB-select__display">
            <span className={`LB-select__placeholder ${
              (effectiveMultiSelect && Array.isArray(value) && value.length > 0) || 
              (!effectiveMultiSelect && selectedOptions) ? 'LB-select__placeholder--has-value' : ''
            }`}>
              {getDisplayText()}
            </span>
            
            {effectiveClearable && ((effectiveMultiSelect && Array.isArray(value) && value.length > 0) || 
                          (!effectiveMultiSelect && selectedOptions)) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="LB-select__clear"
                aria-label="Clear selection"
              >
                ×
              </button>
            )}
            <div className={`LB-select__arrow ${isOpen ? 'LB-select__arrow--open' : ''}`}>
              ▼
            </div>
          </div>
        </div>

        {showTags && renderSelectedTags()}

        {isOpen && (
          <div 
            className={`LB-select__dropdown ${effectiveSearchable ? 'LB-select__dropdown--searchable' : ''}`}
            style={{ maxHeight, minHeight }}
          >
            {effectiveSearchable && (
              <div className="LB-select__search">
                <input
                  ref={inputRef}
                  type="text"
                  className="LB-select__search-input"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setHighlightedIndex(-1);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            {loading ? (
              <div className="LB-select__loading-options">Loading options...</div>
            ) : (
              <div ref={optionsRef} className="LB-select__options">
                {filteredOptions.length === 0 ? (
                  <div className="LB-select__no-options">
                    {noOptionsText}
                  </div>
                ) : (
                  <>
                    {selectAll && effectiveMultiSelect && selectableFilteredOptions.length > 0 && (
                      <div
                        className={`LB-select__option LB-select__option--select-all ${
                          highlightedIndex === -1 ? 'LB-select__option--highlighted' : ''
                        }`}
                        onClick={handleSelectAll}
                        onMouseEnter={() => setHighlightedIndex(-1)}
                      >
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={() => {}}
                          className="LB-select__checkbox"
                        />
                        <span className="LB-select__option-label">
                          {allFilteredSelected ? 'Deselect All' : 'Select All'}
                          {searchTerm && ` (${selectableFilteredOptions.length})`}
                        </span>
                      </div>
                    )}
                    {filteredOptions.map((option, index) => {
                      const isSelected = effectiveMultiSelect
                        ? Array.isArray(value) && value.includes(option.value)
                        : value === option.value;
                      const isHighlighted = index === highlightedIndex;
                      const isDisabled = option.disabled;

                      return (
                        <div
                          key={option.value}
                          className={`LB-select__option ${
                            isSelected ? 'LB-select__option--selected' : ''
                          } ${isHighlighted ? 'LB-select__option--highlighted' : ''} ${
                            isDisabled ? 'LB-select__option--disabled' : ''
                          }`}
                          onClick={() => handleOptionClick(option)}
                          onMouseEnter={() => !isDisabled && setHighlightedIndex(index)}
                          style={isDisabled ? { pointerEvents: 'none' } : {}}
                        >
                          {effectiveMultiSelect && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              disabled={isDisabled}
                              className="LB-select__checkbox"
                            />
                          )}
                          <span className="LB-select__option-label">
                            {renderOption ? renderOption(option) : option.label}
                          </span>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {error && (
        <div className="LB-select__error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default LBSelect;
